import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type CommunicationsService from 'garaje/services/communications-service';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type ReactRendererService from 'garaje/services/react-renderer';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import type { Root } from 'react-dom/client';

interface MessageLogNewArgs {
  locationId?: string;
  locationName?: string;
  redirectRoute: string;
}

export default class CommunicationsMessageLogNewComponent extends Component<MessageLogNewArgs> {
  @service declare reactRenderer: ReactRendererService;
  @service declare router: RouterService;
  @service declare flashMessages: FlashMessagesService;
  @service declare communicationsService: CommunicationsService;
  @service declare workplaceMetrics: WorkplaceMetricsService;

  private root?: Root;

  @action
  async injectComponent(): Promise<void> {
    const canSendEmergencyNotifications = await this.communicationsService.canSendEmergencyNotifications();
    const canSendTakeovers = this.communicationsService.canSendTakeovers;
    const { SendMessage } = await import('reactRichComponents/communication/SendMessage');

    // prevent error from being thrown if element is already destroyed from navigating away
    if (!document.getElementById('react__communications__send-message')) {
      return;
    }

    this.root = await this.reactRenderer.render(SendMessage, 'react__communications__send-message', {
      service: {
        canSelectScimGroups: this.communicationsService.canSelectScimGroups,
        showEmployeeSelect: this.communicationsService.showEmployeeSelect,
        showVisitorSelect: this.communicationsService.showVisitorSelect,
        canSendEmergencyNotifications,
        canSendTakeovers,
        getAudiences: (locationId: string) => this.communicationsService.getAudiences(locationId),
      },
      locationId: this.args.locationId,
      locationName: this.args.locationName,
      onSent: () => this.onSent(),
      onError: (errorMessage, error) => this.onError(errorMessage, error),
      createTemplateUrl: '/communications/templates',
    });
  }

  @action
  destroyComponent(): void {
    if (this.root) {
      this.reactRenderer.unmount(this.root);
      this.root = undefined;
    }
  }

  @action
  onSent(): void {
    void this.router.transitionTo(this.args.redirectRoute);
    this.flashMessages.showAndHideFlash('success', 'Sent successfully');
  }

  @action
  onError(errorMessage: string, error: unknown): void {
    this.flashMessages.showAndHideFlash('error', errorMessage);

    this.workplaceMetrics.logMonitorError({
      event: 'COMMUNICATIONS_SEND_MESSAGE_COMPONENT_ERROR',
      debugExtras: { errorMessage },
      error,
    });
  }
}
